/**
 * Sales-Mix donut — shows the share of approved sales by model_category
 * (SCOOTER / ELECTRIC / MOPED / PREMIUM / MOTORCYCLE / …) for the current
 * branch scope. Always computed against the FULL un-filtered set so the
 * dealership-wide mix stays visible even while other dashboard widgets are
 * narrowed by the active model filter.
 */
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { fmtMoney, fmtNumber } from "@/lib/format";
import { CHART_TOOLTIP_STYLE } from "@/lib/constants";

// Stable palette — kept distinct & accessible. Falls back to gray for any
// category beyond the known ones (custom categories the dealer may add).
const PALETTE = {
  SCOOTER: "#002FA7",       // brand blue
  ELECTRIC: "#00713A",      // emerald
  MOPED: "#FFD700",         // gold
  MOTORCYCLE: "#A35700",    // bronze
  PREMIUM: "#7C3AED",       // violet
  "SUPER PREMIUM": "#B91C1C", // deep red
  COMMERCIAL: "#0EA5E9",    // sky
  OTHER: "#9CA3AF",         // gray
  UNCATEGORISED: "#D1D5DB", // light gray
};

function colorFor(cat) {
  return PALETTE[cat] || "#6B7280";
}

export default function SalesMixDonut({ rows }) {
  const data = (rows || []).filter((r) => r.units > 0);
  const totalUnits = data.reduce((s, r) => s + r.units, 0);
  const totalRevenue = data.reduce((s, r) => s + r.revenue, 0);

  const tooltipFmt = (value, _name, entry) => {
    const r = entry?.payload || {};
    const pct = totalUnits ? ((r.units / totalUnits) * 100).toFixed(1) : 0;
    return [
      `${fmtNumber(r.units)} units · ${pct}%`,
      `${fmtMoney(r.revenue)}`,
    ];
  };

  return (
    <div
      data-testid="dashboard-sales-mix-donut"
      className="bg-canvas-card border border-canvas-line p-5"
    >
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
        03 / Sales Mix
      </div>
      <h3 className="font-display text-lg font-semibold text-ink mb-1">By model category</h3>
      <div className="font-mono text-[10px] text-ink-subtle mb-4">
        {fmtNumber(totalUnits)} units · {fmtMoney(totalRevenue)} total
      </div>

      {data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-xs text-ink-subtle">
          No categorised sales yet. Categorise packages, then run Backfill Model.
        </div>
      ) : (
        <div className="h-56" style={{ minHeight: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="units"
                nameKey="category"
                innerRadius={45}
                outerRadius={75}
                paddingAngle={2}
                stroke="#ffffff"
                strokeWidth={2}
              >
                {data.map((r) => (
                  <Cell key={r.category} fill={colorFor(r.category)} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={tooltipFmt} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }}
                formatter={(v) => v}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
